import React, { useCallback, useEffect, useState } from "react";
import { UserCog, Plus, Search, X, Trash2, Wrench, MapPin, CalendarOff, Clock } from "lucide-react";
import { PageHeader, Button, Input, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const STATUS_TONE: Record<string, "green" | "amber" | "slate"> = { active: "green", on_leave: "amber", inactive: "slate" };
const EMP = ["employee", "contractor", "owner", "other"];

function MemberDrawer({ id, services, locations, onClose, onChanged }: any) {
  const [tab, setTab] = useState("overview");
  const [m, setM] = useState<any>(null);
  const [avail, setAvail] = useState<any[]>([]);
  const [timeOff, setTimeOff] = useState<any[]>([]);
  const load = useCallback(() => {
    api.teamGetMember(id).then(setM).catch((e) => toast.error(formatApiError(e)));
    api.teamGetAvailability(id).then((r) => setAvail(r.windows)).catch(() => {});
    api.teamGetTimeOff(id).then((r) => setTimeOff(r.time_off)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!m) return null;

  const saveField = async (patch: any) => { try { setM(await api.teamUpdateMember(id, patch)); onChanged(); toast.success("Saved"); } catch (e) { toast.error(formatApiError(e)); } };
  const toggleId = (field: "service_ids" | "location_ids", val: string) => {
    const cur: string[] = m[field] || [];
    saveField({ [field]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] });
  };
  const setDay = async (dow: number, start: string, end: string, on: boolean) => {
    const others = avail.filter((w) => w.day_of_week !== dow);
    const next = on ? [...others, { day_of_week: dow, start_time: start, end_time: end, active: true }] : others;
    try { const r = await api.teamSetAvailability(id, next); setAvail(r.windows); toast.success("Availability saved"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const addOff = async () => {
    const date = (document.getElementById("to-date") as HTMLInputElement)?.value;
    if (!date) return toast.error("Pick a date");
    try { await api.teamAddTimeOff(id, { date, all_day: true, type: "time_off" }); load(); toast.success("Time off added"); } catch (e) { toast.error(formatApiError(e)); }
  };

  const TABS = ["overview", "availability", "services", "locations", "time off"];
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="team-drawer">
      <div className="h-full w-full max-w-xl overflow-y-auto bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">{m.display_name}</h2>
            <p className="text-sm text-slate-500">{m.title || "Field team member"} · {m.employment_type}</p>
          </div>
          <button onClick={onClose} data-testid="team-drawer-close"><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="mb-4 flex gap-1 overflow-x-auto rounded-lg border border-slate-200 bg-white p-0.5">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`team-tab-${t.replace(" ", "-")}`}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium capitalize ${tab === t ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-800"}`}>{t}</button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="space-y-3" data-testid="team-overview">
            <label className="block text-sm"><span className="text-slate-500">Status</span>
              <select value={m.status} onChange={(e) => saveField({ status: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" data-testid="team-status-select">
                <option value="active">Active</option><option value="on_leave">On leave</option><option value="inactive">Inactive</option>
              </select></label>
            <div className="grid grid-cols-2 gap-3">
              <Input defaultValue={m.phone || ""} placeholder="Phone" onBlur={(e) => saveField({ phone: e.target.value })} data-testid="team-phone" />
              <Input defaultValue={m.email || ""} placeholder="Email" onBlur={(e) => saveField({ email: e.target.value })} data-testid="team-email" />
              <Input defaultValue={m.title || ""} placeholder="Job title" onBlur={(e) => saveField({ title: e.target.value })} data-testid="team-title" />
              <select defaultValue={m.employment_type} onChange={(e) => saveField({ employment_type: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 capitalize" data-testid="team-emp">
                {EMP.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
              <Input type="number" defaultValue={m.max_jobs_per_day ?? ""} placeholder="Max jobs/day" onBlur={(e) => saveField({ max_jobs_per_day: Number(e.target.value) || null })} data-testid="team-maxjobs" />
              <Input type="number" defaultValue={m.buffer_minutes ?? ""} placeholder="Buffer (min)" onBlur={(e) => saveField({ buffer_minutes: Number(e.target.value) || null })} data-testid="team-buffer" />
            </div>
          </div>
        )}

        {tab === "availability" && (
          <div className="space-y-2" data-testid="team-availability">
            <p className="text-xs text-slate-500">Recurring weekly working hours. These feed the availability engine (technician-aware slots go live in Phase 2).</p>
            {DAYS.map((d, dow) => {
              const w = avail.find((x) => x.day_of_week === dow);
              return (
                <div key={dow} className="flex items-center gap-2" data-testid={`avail-day-${dow}`}>
                  <span className="w-24 text-sm text-slate-700">{d}</span>
                  <input type="checkbox" checked={!!w} onChange={(e) => setDay(dow, w?.start_time || "08:00", w?.end_time || "17:00", e.target.checked)} data-testid={`avail-toggle-${dow}`} />
                  <input type="time" defaultValue={w?.start_time || "08:00"} disabled={!w} onBlur={(e) => setDay(dow, e.target.value, w?.end_time || "17:00", true)} className="rounded border border-slate-300 px-2 py-1 text-sm disabled:opacity-40" />
                  <span className="text-slate-400">–</span>
                  <input type="time" defaultValue={w?.end_time || "17:00"} disabled={!w} onBlur={(e) => setDay(dow, w?.start_time || "08:00", e.target.value, true)} className="rounded border border-slate-300 px-2 py-1 text-sm disabled:opacity-40" />
                </div>
              );
            })}
          </div>
        )}

        {tab === "services" && (
          <div className="space-y-2" data-testid="team-services">
            <p className="text-xs text-slate-500">Only services checked here will let this member be offered for that appointment (Phase 2 enforces it in the engine).</p>
            {services.length === 0 && <EmptyState icon={Wrench} title="No services" description="Add services in the Services page first." />}
            {services.map((s: any) => (
              <label key={s.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`svc-${s.id}`}>
                <input type="checkbox" checked={(m.service_ids || []).includes(s.id)} onChange={() => toggleId("service_ids", s.id)} />
                {s.name} <span className="ml-auto text-xs text-slate-400">{s.duration_minutes || 60} min</span>
              </label>
            ))}
          </div>
        )}

        {tab === "locations" && (
          <div className="space-y-2" data-testid="team-locations">
            {locations.length === 0 && <EmptyState icon={MapPin} title="No locations" description="Add locations in the Locations page first." />}
            {locations.map((l: any) => (
              <label key={l.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`loc-${l.id}`}>
                <input type="checkbox" checked={(m.location_ids || []).includes(l.id)} onChange={() => toggleId("location_ids", l.id)} />
                {l.name}
              </label>
            ))}
          </div>
        )}

        {tab === "time off" && (
          <div className="space-y-3" data-testid="team-timeoff">
            <div className="flex items-end gap-2">
              <input id="to-date" type="date" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="timeoff-date" />
              <Button size="sm" onClick={addOff} data-testid="timeoff-add"><Plus className="h-4 w-4" /> Add time off</Button>
            </div>
            {timeOff.length === 0 ? <EmptyState icon={CalendarOff} title="No time off" description="Vacations, PTO and blocked days appear here." /> :
              timeOff.map((t) => (
                <div key={t.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`timeoff-${t.id}`}>
                  <span>{t.date} · <span className="capitalize text-slate-500">{t.type}</span>{t.all_day ? " · All day" : ` · ${t.start_time}–${t.end_time}`}</span>
                  <button onClick={async () => { await api.teamDeleteTimeOff(id, t.id); load(); }} data-testid={`timeoff-del-${t.id}`}><Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" /></button>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AddMemberModal({ onClose, onSaved }: any) {
  const [f, setF] = useState({ first_name: "", last_name: "", title: "", employment_type: "employee", phone: "" });
  const save = async () => {
    if (!f.first_name.trim()) return toast.error("First name is required");
    try { await api.teamCreateMember(f); toast.success("Team member added"); onSaved(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="add-member-modal">
      <div className="w-full max-w-md rounded-xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-4 text-lg font-semibold">Add field team member</h2>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="First name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="add-first" />
          <Input placeholder="Last name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="add-last" />
          <Input placeholder="Job title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="add-title" />
          <select value={f.employment_type} onChange={(e) => setF({ ...f, employment_type: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 capitalize" data-testid="add-emp">
            {EMP.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <Input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="col-span-2" data-testid="add-phone" />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="add-member-save">Add member</Button>
        </div>
      </div>
    </div>
  );
}

export default function Team() {
  const [tab, setTab] = useState<"field" | "platform">("field");
  const [members, setMembers] = useState<any[] | null>(null);
  const [platform, setPlatform] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(() => {
    const params: Record<string, string> = {};
    if (statusF) params.status = statusF;
    if (q) params.q = q;
    api.teamMembers(params).then((r) => setMembers(r.members)).catch((e) => toast.error(formatApiError(e)));
  }, [statusF, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.svcList().then((s: any) => setServices(s)).catch(() => {});
    api.locList().then((l: any) => setLocations(l)).catch(() => {});
    api.teamPlatformUsers().then((r) => setPlatform(r.users)).catch(() => {});
  }, []);

  return (
    <div data-testid="page-team" className="space-y-6">
      <PageHeader title="Team" testid="team-header" description="Your field technicians and the people who can log into ResFil."
        action={tab === "field" ? <Button onClick={() => setShowAdd(true)} data-testid="add-member-btn"><Plus className="h-4 w-4" /> Add team member</Button> : undefined} />

      <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-0.5 w-fit">
        {(["field", "platform"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`team-view-${t}`}
            className={`rounded-md px-4 py-1.5 text-sm font-medium ${tab === t ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-800"}`}>
            {t === "field" ? "Field Team" : "Platform Users"}
          </button>
        ))}
      </div>

      {tab === "field" ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5">
              <Search className="h-4 w-4 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name…" className="text-sm outline-none" data-testid="team-search" />
            </div>
            <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm" data-testid="team-status-filter">
              <option value="">All statuses</option><option value="active">Active</option><option value="on_leave">On leave</option><option value="inactive">Inactive</option>
            </select>
          </div>
          {members === null ? <LoadingBlock rows={4} /> : members.length === 0 ? (
            <EmptyState icon={UserCog} title="No team members yet" description="Add your first technician to power AI scheduling and dispatch." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {members.map((m) => (
                <button key={m.id} onClick={() => setOpenId(m.id)} data-testid={`member-card-${m.id}`}
                  className="rounded-xl border border-slate-200 bg-white p-4 text-left transition-colors hover:border-blue-300">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-slate-900">{m.display_name}</p>
                    <StatusBadge tone={STATUS_TONE[m.status]}>{m.status.replace("_", " ")}</StatusBadge>
                  </div>
                  <p className="mt-0.5 text-sm text-slate-500">{m.title || "Field team"}</p>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-400">
                    <span className="capitalize">{m.employment_type}</span>
                    <span>· {(m.service_ids || []).length} services</span>
                    <span>· {(m.location_ids || []).length} locations</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white" data-testid="platform-users">
          <div className="border-b border-slate-100 px-5 py-3 text-sm text-slate-500">People with a ResFil login for this business. Field technicians don't need a login unless you invite them.</div>
          {platform.length === 0 ? <div className="p-6 text-center text-sm text-slate-500">No platform users.</div> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500">{["Name", "Email", "Role", "Status"].map((h) => <th key={h} className="px-5 py-2">{h}</th>)}</tr></thead>
              <tbody>{platform.map((u) => (
                <tr key={u.profile_id} className="border-t border-slate-50" data-testid={`platform-user-${u.profile_id}`}>
                  <td className="px-5 py-2 font-medium text-slate-900">{u.name}</td>
                  <td className="px-5 py-2 text-slate-500">{u.email}</td>
                  <td className="px-5 py-2 capitalize">{u.role}</td>
                  <td className="px-5 py-2"><StatusBadge tone={u.invitation_status === "accepted" ? "green" : "amber"}>{u.invitation_status}</StatusBadge></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {showAdd && <AddMemberModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {openId && <MemberDrawer id={openId} services={services} locations={locations} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
